import { useState, useEffect, useCallback } from 'react';

interface FilterState {
  searchTerm: string;
  selectedCategories: string[];
  selectedSubCategories: string[];
  order: string;
  itemsPerPage: number;
  page: number;
}

const DEFAULT_FILTER_STATE: FilterState = {
  searchTerm: '',
  selectedCategories: [],
  selectedSubCategories: [],
  order: 'ASC',
  itemsPerPage: 15,
  page: 1,
};

const STORAGE_KEY = 'instru-mentor-filters';

export const usePersistentFilters = (language: string) => {
  const [filterState, setFilterState] = useState<FilterState>(DEFAULT_FILTER_STATE);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load filters from localStorage on mount
  useEffect(() => {
    const savedFilters = localStorage.getItem(`${STORAGE_KEY}-${language}`);
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilterState({
          ...DEFAULT_FILTER_STATE,
          ...parsed,
        });
      } catch (error) {
        console.warn('Failed to parse saved filters:', error);
        setFilterState(DEFAULT_FILTER_STATE);
      }
    }
    setIsInitialized(true);
  }, [language]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(`${STORAGE_KEY}-${language}`, JSON.stringify(filterState));
    }
  }, [filterState, language, isInitialized]);

  const updateFilterState = useCallback((updates: Partial<FilterState>) => {
    setFilterState(prev => ({ ...prev, ...updates }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilterState(DEFAULT_FILTER_STATE);
    localStorage.removeItem(`${STORAGE_KEY}-${language}`);
  }, [language]);

  const clearFiltersForCategory = useCallback(() => {
    updateFilterState({
      selectedCategories: [],
      selectedSubCategories: [],
      page: 1,
    });
  }, [updateFilterState]);

  return {
    filterState,
    updateFilterState,
    resetFilters,
    clearFiltersForCategory,
    isInitialized,
  };
};